import {gql, OperationVariables} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import type {GetTagSchemaSummariesListing} from 'schemas/__generated/GetTagSchemaSummariesListing';
import {TAG_SCHEMA_SUMMARIES_QUERY} from 'schemas/useTagSchemaSummariesListingQuery';
import {toOligoAwareFolderItemTypeGraphQL} from 'tag-schema/util/ModelTypeConversions';
import {assertNever} from 'util/Types';

import type {RoutineStudySchemas} from './__generated/RoutineStudySchemas';

export interface RoutineSchemaOption {
  id: string;
  name: string;
  owner?: string;
}

/**
 * Describes where a routine's schema options come from.
 *
 * Most object types use the shared tag-schema listing query, filtered server-side by their
 * `OligoAwareFolderItemType` (the `oligoAwareTagSchema` source). Other object types do not have tag
 * schemas and instead expose their own top-level GraphQL field (e.g. `studySchemas`), so they declare a
 * dedicated source kind that maps to that query. To support a new object type, add a source kind here and
 * a matching case in {@link resolveRoutineSchemaQuery} (the `assertNever` there enforces this).
 */
export type RoutineSchemaSource =
  | {kind: 'oligoAwareTagSchema'; folderItemType: OligoAwareFolderItemType}
  | {kind: 'studySchema'};

export const ROUTINE_STUDY_SCHEMAS_QUERY: DocumentNode = gql`
  query RoutineStudySchemas @skipAudit @engComponent(name: LLM) {
    studySchemas {
      id
      name
      organization {
        id
        name
      }
    }
  }
`;

export interface ResolvedRoutineSchemaQuery {
  query: DocumentNode;
  variables: OperationVariables;
  extractSchemas: (data: unknown) => Array<RoutineSchemaOption>;
}

function toSchemaOptions(
  schemas: ReadonlyArray<{id: string; name: string; organization?: {name: string}}> | undefined
): Array<RoutineSchemaOption> {
  return (schemas ?? []).map((schema) => ({
    id: schema.id,
    name: schema.name,
    owner: schema.organization?.name,
  }));
}

/**
 * Resolves a {@link RoutineSchemaSource} to the GraphQL query, variables, and a function that extracts the
 * `{id, name}` options from the query's response.
 */
export function resolveRoutineSchemaQuery(source: RoutineSchemaSource): ResolvedRoutineSchemaQuery {
  switch (source.kind) {
    case 'oligoAwareTagSchema':
      return {
        query: TAG_SCHEMA_SUMMARIES_QUERY,
        variables: {
          searchInput: {
            registryId: null,
            oligoAwareFolderItemTypes: [toOligoAwareFolderItemTypeGraphQL(source.folderItemType)],
          },
        },
        extractSchemas: (data) =>
          toSchemaOptions((data as GetTagSchemaSummariesListing | undefined)?.tagSchemasListing?.tagSchemas),
      };
    case 'studySchema':
      return {
        query: ROUTINE_STUDY_SCHEMAS_QUERY,
        variables: {},
        extractSchemas: (data) => toSchemaOptions((data as RoutineStudySchemas | undefined)?.studySchemas),
      };
    default:
      return assertNever(source);
  }
}
